import { Plus, Gamepad2, Clock, Loader2, AlertCircle, ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useProjectsStore } from '../store/useProjectsStore'
import CreateGameModal from '../components/common/CreateGameModal'
import { templateConfigService, type TemplateOption } from '../services/templateConfigService'
import type { Game } from '../types/api'
import { FILL_GRADIENT } from '../constants/gradients'
import { DevelopmentGameCard } from '../components/projects/DevelopmentGameCard'

// Import template images
import gameNone from '../assets/templates/game-none.png'
import gameTicTacToe from '../assets/templates/game-tic_tac_toe.png'
import gameRockPaperScissors from '../assets/templates/game-rock_paper_scissors.png'
import gameCoinFlip from '../assets/templates/game-coin_flip.png'

// Image mapping for templates
const templateImageMap: Record<string, any> = {
  blank: gameNone,
  tictactoe: gameTicTacToe,
  rockpaperscissors: gameRockPaperScissors,
  coinflip: gameCoinFlip,
  trivia: gameNone,
  poll: gameNone
}

// Helper function to format time
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
  return date.toLocaleDateString()
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const [showCreateGameModal, setShowCreateGameModal] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined)
  const [initialTab, setInitialTab] = useState<'prompt' | 'template' | undefined>(undefined)
  const [templates, setTemplates] = useState<TemplateOption[]>([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
  
  const {
    recentProjects,
    isLoadingRecentProjects,
    recentProjectsError,
    fetchRecentProjects,
  } = useProjectsStore()

  // Fetch data on component mount
  useEffect(() => {
    console.log('📊 DashboardPage: Fetching initial data...')
    fetchRecentProjects(5)
  }, [])

  // Fetch templates from server
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true)
        const config = await templateConfigService.getConfig()
        // Filter out "blank" and take first 3 templates for dashboard
        const filteredTemplates = config.templates.filter(t => t.id !== 'blank').slice(0, 3)
        setTemplates(filteredTemplates)
        console.log('✅ Dashboard templates loaded:', filteredTemplates)
      } catch (error) {
        console.error('❌ Failed to load templates:', error)
      } finally {
        setIsLoadingTemplates(false)
      }
    }
    
    fetchTemplates()
  }, [])

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Section 1: Create New Game */}
      <section>
        {/* Big Create Button */}
        <button 
          onClick={() => setShowCreateGameModal(true)}
          className="w-full text-white font-semibold text-xl py-6 px-8 rounded-2xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 mb-6"
          style={{
            background: FILL_GRADIENT,
          }}
        >
          <Plus className="w-7 h-7" />
          Create New Game
        </button>

        {/* Templates Section */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">or start from template</p>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
        </div>

        {isLoadingTemplates ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {templates.map((template) => {
                const templateImage = templateImageMap[template.id] || gameNone
                
                return (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplateId(template.id)
                      setShowCreateGameModal(true)
                    }}
                    className="relative rounded-[15px] text-left group p-[2px] bg-gray-200 dark:bg-gray-700"
                  >
                    {/* Gradient border overlay */}
                    <div 
                      className="absolute inset-0 rounded-[15px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{ background: 'linear-gradient(90deg, rgb(252, 131, 2) 0%, #FF00CF 100%)' }}
                    />
                    <div className="relative bg-white dark:bg-dark-surface rounded-[13px] p-6 h-full">
                      <div className="rounded-lg h-48 mb-4 overflow-hidden">
                        <img 
                          src={templateImage} 
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-dark-text text-lg text-center">
                        {template.name}
                      </h3>
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="mt-6">
              <button 
                onClick={() => {
                  setSelectedTemplateId(undefined)
                  setInitialTab('template')
                  setShowCreateGameModal(true)
                }}
                className="w-full px-6 py-2.5 bg-white dark:bg-dark-surface border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              >
                See More Templates
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </section>

      {/* Section 2: Recently Edited */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">Recently Edited</h2>
          </div>
          <Link 
            to="/projects?tab=in-development"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            See more
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Error Message */}
        {recentProjectsError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-300">
                Error loading projects: {recentProjectsError}
              </p>
            </div>
          </div>
        )}

        {/* Projects List */}
        <div className="space-y-3">
          {isLoadingRecentProjects ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading projects...</span>
            </div>
          ) : recentProjects.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl">
              <Gamepad2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-dark-text-muted font-medium">No projects found</p>
              <p className="text-sm text-gray-400 dark:text-dark-text-muted mt-1">
                Create your first project to get started
              </p>
            </div>
          ) : (
            recentProjects.map((project: Game, index) => (
              <DevelopmentGameCard
                key={`recent-project-${project.id}-${index}`}
                project={project}
                index={index}
                onArchive={() => {}} // Dashboard doesn't need these actions
                onEdit={() => {}}
                onDuplicate={async () => {}}
                isActionInProgress={false}
                formatTimeAgo={formatTimeAgo}
              />
            ))
          )}
        </div>
      </section>

      {/* Create Game Modal */}
      <CreateGameModal
        isOpen={showCreateGameModal}
        onClose={() => {
          setShowCreateGameModal(false)
          setSelectedTemplateId(undefined) // Clear selection when modal closes
          setInitialTab(undefined)
        }}
        initialTemplate={selectedTemplateId}
        initialTab={initialTab}
      />
    </div>
  )
}

export default DashboardPage 